import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "@mantine/core";
import {API_CHECK_TIME, API_SUCCESS} from "./types";
import {chatApi} from "./features/chat/chatApi";
import {DenyPopover} from "./DenyPopover";
import IconX from "./assets/x.svg?react";
import {useTranslation} from "react-i18next";
import {selectAuthStatus} from "./features/auth/sessionAuthSlice";

const AUTH_REDIRECT_TS_KEY = "auth_redirect_ts";
const LOOP_BLOCKED_GRACE_MS = 500;

const buildLoginHref = () =>
    "/auth/dib_login?returnUrl=" +
    encodeURIComponent(window.location.pathname + window.location.search);

const SessionExpired = () => {
    const {t} = useTranslation();
    const checkSessionTimerRef = useRef<NodeJS.Timeout | null>(null);
    const authStatus = useSelector(selectAuthStatus);
    const [getLoginStatus] = chatApi.endpoints.pong.useLazyQuery();
    const [showLoopBlocked, setShowLoopBlocked] = useState(false);

    const startCheckSessionTimer = () => {
        if (checkSessionTimerRef.current) {
            clearTimeout(checkSessionTimerRef.current);
        }
        checkSessionTimerRef.current = setTimeout(() => {
            getLoginStatus();
        }, API_CHECK_TIME);
    };

    useEffect(() => {
        document.addEventListener(API_SUCCESS, startCheckSessionTimer);
        return () => {
            document.removeEventListener(API_SUCCESS, startCheckSessionTimer);
        };
    }, []);

    useEffect(() => {
        if (authStatus !== "expired") {
            return;
        }

        const performRedirect = () => {
            sessionStorage.setItem(AUTH_REDIRECT_TS_KEY, Date.now().toString());
            window.location.href = buildLoginHref();
        };

        // If the user is not focused on the tab, wait for them to focus on the tab before triggering login
        if (document.hidden) {
            window.addEventListener("focus", performRedirect);
            return () => {
                window.removeEventListener("focus", performRedirect);
            };
        } else {
            performRedirect();
        }
    }, [authStatus]);

    useEffect(() => {
        if (authStatus !== "loopBlocked") {
            setShowLoopBlocked(false);
            return;
        }
        const timer = setTimeout(() => {
            setShowLoopBlocked(true);
        }, LOOP_BLOCKED_GRACE_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [authStatus]);

    if (authStatus === "loopBlocked" && showLoopBlocked) {
        return (
            <DenyPopover
                icon={<IconX />}
                title={t("sessionExpired.title")}
                description={t("sessionExpired.description")}
                action={
                    <Button component="a" href="/auth/logout">
                        {t("sessionExpired.signOut")}
                    </Button>
                }
            />
        );
    }

    return <></>;
};

export default SessionExpired;
